import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { debugLog, debugError } from '@/config/debug';

interface TranscriptLine {
  text: string;
  speaker?: number;
  start?: number;
  end?: number;
}

interface TranscriptionData {
  text: string;
  speaker?: number;
  start?: number;
  end?: number;
}

interface UseDeepgramProps {
  apiKey: string;
  onTranscriptionReceived?: (transcription: TranscriptionData) => void;
  onError?: (error: string) => void;
  onMicrophoneNotFound?: () => void;
  onMicrophoneVerified?: () => Promise<number | null>;
}

export function useDeepgram({ apiKey, onTranscriptionReceived, onError, onMicrophoneNotFound, onMicrophoneVerified }: UseDeepgramProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState<TranscriptLine[]>([]);
  const [transcriptionText, setTranscriptionText] = useState("");
  const [error, setError] = useState<string>("");
  const [connectionStatus, setConnectionStatus] = useState<string>("disconnected");
  const { i18n } = useTranslation();

  // Refs for stable values that don't need to trigger re-renders
  const socketRef = useRef<WebSocket | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isManualCloseRef = useRef(false);
  const lastFinalTranscriptRef = useRef<string>('');
  const isMountedRef = useRef(true);
  const isRecordingRef = useRef(false);
  const isInitializingRef = useRef(false);

  // Stable callback refs to prevent useEffect dependencies from changing
  const onTranscriptionReceivedRef = useRef(onTranscriptionReceived);
  const onErrorRef = useRef(onError);
  const onMicrophoneNotFoundRef = useRef(onMicrophoneNotFound);
  const onMicrophoneVerifiedRef = useRef(onMicrophoneVerified);

  // Update refs when props change
  useEffect(() => {
    onTranscriptionReceivedRef.current = onTranscriptionReceived;
  }, [onTranscriptionReceived]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onMicrophoneNotFoundRef.current = onMicrophoneNotFound;
  }, [onMicrophoneNotFound]);

  useEffect(() => {
    onMicrophoneVerifiedRef.current = onMicrophoneVerified;
  }, [onMicrophoneVerified]);

  // Get supported MIME type for the current browser
  const getSupportedMimeType = () => {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
      'audio/wav'
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        debugLog('DEEPGRAM', '✅ Using MIME type:', type);
        return type;
      }
    }

    debugLog('DEEPGRAM', '⚠️ No specific MIME type supported, using default');
    return undefined;
  };

  // Get language code for Deepgram
  const getDeepgramLanguage = useCallback(() => {
    const currentLang = i18n.language;
    const baseLang = currentLang.split('-')[0].toLowerCase();
    
    switch (baseLang) {
      case 'es':
        return 'es';
      case 'pt':
        return 'pt';
      case 'en':
        return 'en';
      default:
        return 'es';
    }
  }, [i18n.language]);

  // Check if microphones are available
  const checkMicrophoneAvailability = useCallback(async () => {
    debugLog('DEEPGRAM', '🎤 [DEEPGRAM] Checking microphone availability...');
    
    try {
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        debugLog('DEEPGRAM', '🎤 [DEEPGRAM] getUserMedia not supported');
        return false;
      }

      // Check for available microphones
      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphones = devices.filter(device => device.kind === 'audioinput');
      
      debugLog('DEEPGRAM', '🎤 [DEEPGRAM] Found microphones:', microphones.length);
      
      if (microphones.length === 0) {
        debugLog('DEEPGRAM', '🎤 [DEEPGRAM] No microphones found');
        return false;
      }

      // Try to actually access the microphone
      try {
        const testStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        testStream.getTracks().forEach(track => track.stop());
        debugLog('DEEPGRAM', '🎤 [DEEPGRAM] Microphone access successful');
        return true;
      } catch (error: any) {
        debugLog('DEEPGRAM', '🎤 [DEEPGRAM] Microphone access failed:', error.name);
        return false;
      }
    } catch (error) {
      debugLog('DEEPGRAM', '🎤 [DEEPGRAM] Error checking microphones:', error);
      return false;
    }
  }, []);

  // Immediate transcription handler - no debouncing
  const handleTranscriptionReceived = useCallback((text: string, speaker?: number) => {
    if (!isMountedRef.current || !text || text.trim().length === 0) {
      return;
    }

    debugLog('DEEPGRAM', '🎯 Sending immediate transcription:', text, 'Speaker:', speaker);

    const newLine: TranscriptLine = {
      text: text,
      speaker: speaker,
      start: Date.now(),
      end: Date.now(),
    };

    setTranscript(prev => {
      const updated = [...prev, newLine];
      const allFinalTexts = updated.map(t => t.text).join(' ');
      setTranscriptionText(allFinalTexts);
      return updated;
    });

    // Send immediately to parent component
    onTranscriptionReceivedRef.current?.({
      text: text,
      speaker: speaker,
      start: Date.now(),
      end: Date.now(),
    });
  }, []);

  const forceCleanup = useCallback(() => {
    debugLog('DEEPGRAM', "🔥 Force cleanup - stopping all connections...");
    
    // Set manual close flag to prevent error toast
    isManualCloseRef.current = true;

    // Clear any pending cleanup timeout
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }

    // Stop MediaRecorder immediately
    if (mediaRecorderRef.current) {
      try {
        if (mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
        debugLog('DEEPGRAM', "✅ MediaRecorder stopped");
      } catch (err) {
        debugError('DEEPGRAM', "❌ Error stopping MediaRecorder:", err);
      }
      mediaRecorderRef.current = null;
    }

    // Stop all audio tracks immediately
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
          debugLog('DEEPGRAM', "✅ Audio track stopped");
        } catch (err) {
          debugError('DEEPGRAM', "❌ Error stopping track:", err);
        }
      });
      streamRef.current = null;
    }

    // Close WebSocket connection immediately
    if (socketRef.current) {
      try {
        if (socketRef.current.readyState === WebSocket.OPEN || 
            socketRef.current.readyState === WebSocket.CONNECTING) {
          socketRef.current.close(1000, "Manual close");
          debugLog('DEEPGRAM', "✅ WebSocket close initiated");
        }
      } catch (err) {
        debugError('DEEPGRAM', "❌ Error closing WebSocket:", err);
      }
      socketRef.current = null;
    }

    // Update states immediately to "disconnected" to avoid showing intermediate states
    setIsRecording(false);
    isRecordingRef.current = false;
    setConnectionStatus("disconnected");
    setError(""); // Clear any existing errors
    lastFinalTranscriptRef.current = ''; // Reset last transcript
    isInitializingRef.current = false;
    debugLog('DEEPGRAM', "✅ Force cleanup completed");
  }, []);

  // Stable stop recording function
  const stopRecording = useCallback(() => {
    if (!isRecordingRef.current || isInitializingRef.current) {
      debugLog('DEEPGRAM', "🛑 Stop recording called but not recording or initializing");
      return;
    }

    debugLog('DEEPGRAM', "🛑 Stopping recording...");
    forceCleanup();
  }, [forceCleanup]);

  // Stable start recording function
  const startRecording = useCallback(async () => {
    // Prevent multiple simultaneous initializations
    if (isRecordingRef.current || isInitializingRef.current) {
      debugLog('DEEPGRAM', "🚀 Start recording called but already recording or initializing");
      return;
    }

    debugLog('DEEPGRAM', "🚀 Starting recording...");
    isInitializingRef.current = true;
    
    // Reset manual close flag
    isManualCloseRef.current = false;
    lastFinalTranscriptRef.current = '';
    
    // Force cleanup any existing connections first
    forceCleanup();
    
    // Wait a moment for cleanup to complete
    await new Promise(resolve => setTimeout(resolve, 200));

    if (!isMountedRef.current) {
      isInitializingRef.current = false;
      return;
    }

    if (!apiKey || apiKey.trim() === '') {
      const errorMsg = "Se requiere una clave API de Deepgram válida";
      debugError('DEEPGRAM', errorMsg);
      onErrorRef.current?.(errorMsg);
      isInitializingRef.current = false;
      return;
    }

    try {
      setError("");
      setConnectionStatus("connecting");

      debugLog('DEEPGRAM', "🎤 Checking microphone availability before starting...");
      
      const microphoneAvailable = await checkMicrophoneAvailability();
      
      if (!microphoneAvailable) {
        debugLog('DEEPGRAM', "🎤 [DEEPGRAM] CALLING onMicrophoneNotFound - no microphones available");
        onMicrophoneNotFoundRef.current?.();
        isInitializingRef.current = false;
        return;
      }

      debugLog('DEEPGRAM', "🎤 Requesting microphone access...");
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
          channelCount: 1
        }
      });
      
      if (!isMountedRef.current) {
        stream.getTracks().forEach(track => track.stop());
        isInitializingRef.current = false;
        return;
      }

      streamRef.current = stream;
      debugLog('DEEPGRAM', "✅ Got microphone access");

      // Call the microphone verified callback to create analysis
      if (onMicrophoneVerifiedRef.current) {
        debugLog('DEEPGRAM', "🎤 [DEEPGRAM] CALLING onMicrophoneVerified - microphone verified successfully");
        await onMicrophoneVerifiedRef.current();
      }

      const deepgramLanguage = getDeepgramLanguage();
      debugLog('DEEPGRAM', "🌍 Using language:", deepgramLanguage);

      const wsUrl = `wss://api.deepgram.com/v1/listen?language=${deepgramLanguage}&model=nova-2&smart_format=true&interim_results=false&sample_rate=16000&channels=1&diarize=true&punctuate=true&utterances=true`;
      
      debugLog('DEEPGRAM', "🔗 Connecting to:", wsUrl);
      
      const socket = new WebSocket(wsUrl, ["token", apiKey]);
      socketRef.current = socket;

      socket.onopen = () => {
        if (!isMountedRef.current) {
          socket.close();
          isInitializingRef.current = false;
          return;
        }

        debugLog('DEEPGRAM', "✅ WebSocket connected successfully");
        setConnectionStatus("connected");

        try {
          const supportedMimeType = getSupportedMimeType();
          const mediaRecorderOptions = supportedMimeType ? { mimeType: supportedMimeType } : {};
          
          const mediaRecorder = new MediaRecorder(stream, mediaRecorderOptions);
          mediaRecorderRef.current = mediaRecorder;

          mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0 && socket.readyState === WebSocket.OPEN) {
              socket.send(event.data);
            }
          };

          mediaRecorder.onerror = (event) => {
            debugError('DEEPGRAM', "❌ MediaRecorder error:", event);
            if (!isManualCloseRef.current && isMountedRef.current) {
              onErrorRef.current?.("Error en la grabación de audio");
            }
          };

          mediaRecorder.onstop = () => {
            debugLog('DEEPGRAM', "🛑 MediaRecorder stopped");
          };

          // Reduced interval for more frequent transcription updates
          mediaRecorder.start(500); // 500ms intervals instead of 1000ms
          setIsRecording(true);
          isRecordingRef.current = true;
          setConnectionStatus("recording");
          isInitializingRef.current = false;
          debugLog('DEEPGRAM', "🎙️ Recording started successfully");
        } catch (err) {
          debugError('DEEPGRAM', "❌ Error creating MediaRecorder:", err);
          if (!isManualCloseRef.current && isMountedRef.current) {
            onErrorRef.current?.("Error al configurar la grabación");
          }
          forceCleanup();
          isInitializingRef.current = false;
        }
      };

      socket.onmessage = (message) => {
        if (!isMountedRef.current) return;

        try {
          const data = JSON.parse(message.data);
          debugLog('DEEPGRAM', '🎤 Raw Deepgram data received:', data);

          if (data.channel?.alternatives?.[0]?.transcript) {
            const text = data.channel.alternatives[0].transcript.trim();
            
            // Only process final results, reduced minimum length to 1
            if (text && data.is_final && text.length >= 1) {
              // Get speaker information
              const speaker = data.channel.alternatives[0].words?.[0]?.speaker;
              
              // Avoid processing identical consecutive transcriptions
              const currentTranscript = `${speaker || 'unknown'}-${text}`;
              if (currentTranscript === lastFinalTranscriptRef.current) {
                debugLog('DEEPGRAM', '⚠️ Skipping duplicate:', currentTranscript);
                return;
              }
              lastFinalTranscriptRef.current = currentTranscript;

              debugLog('DEEPGRAM', "📝 Processing final transcription:", text, "Speaker:", speaker);
              
              // Send immediately, no debouncing
              handleTranscriptionReceived(text, speaker);
            } else {
              debugLog('DEEPGRAM', '⚠️ Skipping transcription - not final or too short:', { 
                text: text, 
                is_final: data.is_final, 
                length: text.length 
              });
            }
          }
        } catch (err) {
          debugError('DEEPGRAM', "❌ Error processing message:", err);
        }
      };

      socket.onerror = (error) => {
        debugError('DEEPGRAM', "❌ WebSocket error:", error);
        if (!isManualCloseRef.current && isMountedRef.current) {
          setConnectionStatus("error");
          onErrorRef.current?.("Error de conexión con el servicio de transcripción");
        }
        forceCleanup();
        isInitializingRef.current = false;
      };

      socket.onclose = (event) => {
        debugLog('DEEPGRAM', "🔌 WebSocket closed:", event.code, event.reason);
        
        // Only update states and show errors if it wasn't a manual close
        if (!isManualCloseRef.current && isMountedRef.current) {
          setConnectionStatus("disconnected");
          setIsRecording(false);
          isRecordingRef.current = false;
          
          if (event.code === 1006) {
            onErrorRef.current?.("Conexión perdida inesperadamente");
          } else if (event.code === 1002) {
            onErrorRef.current?.("Error de protocolo WebSocket");
          } else if (event.code === 4008) {
            onErrorRef.current?.("Clave API de Deepgram inválida");
          } else if (event.code === 4009) {
            onErrorRef.current?.("Clave API de Deepgram insuficiente");
          }
        }
        isInitializingRef.current = false;
      };

    } catch (error: any) {
      debugError('DEEPGRAM', "❌ Error starting recording:", error);
      debugError('DEEPGRAM', "❌ Error name:", error.name);
      debugError('DEEPGRAM', "❌ Error message:", error.message);
      debugError('DEEPGRAM', "❌ Full error object:", error);
      
      if (!isManualCloseRef.current && isMountedRef.current) {
        setConnectionStatus("error");
        
        debugLog('DEEPGRAM', "🎤 [DEEPGRAM] CALLING onMicrophoneNotFound - error in microphone access");
        onMicrophoneNotFoundRef.current?.();
      }
      forceCleanup();
      isInitializingRef.current = false;
    }
  }, [apiKey, forceCleanup, handleTranscriptionReceived, getDeepgramLanguage, checkMicrophoneAvailability]);

  const clearTranscription = useCallback(() => {
    setTranscriptionText("");
    setTranscript([]);
    lastFinalTranscriptRef.current = '';
  }, []);

  // Track component mount state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      debugLog('DEEPGRAM', "🧹 Cleanup useDeepgram");
      isMountedRef.current = false;
      forceCleanup();
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
    };
  }, [forceCleanup]);

  return {
    startRecording,
    stopRecording,
    isRecording,
    transcriptionText,
    transcript,
    clearTranscription,
    connectionStatus,
    error,
  };
}
